// ** React Imports

import { useAtom } from 'jotai'

// ** Next Import
import { useRouter } from 'next/router'
import { PropsWithChildren, ReactNode, useState } from 'react'
import BlankLayout from 'src/@core/layouts/BlankLayout'
// ** Types
import type { ACLObj, AppAbility } from 'src/configs/acl'
// ** Config Import
import { buildAbilityFor } from 'src/configs/acl'
// ** Hooks
import { useAuth } from 'src/hooks/useAuth'
// ** Context Imports
import { AbilityContext } from 'src/layouts/components/acl/Can'
// ** Component Import
import NotAuthorized from 'src/pages/401'
import { navItemsAtom } from '@/atoms'

interface AclGuardProps {
  children: ReactNode
  guestGuard: boolean
  aclAbilities: ACLObj
}

const AclGuard: React.FC<PropsWithChildren<AclGuardProps>> = (props: AclGuardProps) => {
  // ** Props
  const { aclAbilities, children, guestGuard } = props

  // ** States
  const [ability, setAbility] = useState<AppAbility | undefined>(undefined)

  // ** Hooks
  const auth = useAuth()
  const router = useRouter()

  const [navItems] = useAtom(navItemsAtom)

  // ** Variables
  const menus = navItems.flatMap(nav => nav.children)
  const currentPathname = router.pathname

  const isAuthorized = menus.filter(menu => currentPathname.includes(menu?.path)).length > 0

  // If guestGuard is true and user is not logged in or its an error page, render the page without checking access
  if (guestGuard || router.route === '/404' || router.route === '/500' || router.route === '/') {
    return <>{children}</>
  }

  // User is logged in, build ability for the user based on his role
  if (auth.user && auth.user.role && !ability) {
    setAbility(buildAbilityFor(auth.user.role, aclAbilities.subject))
  }

  // Check the access of current user and render pages
  if (ability && ability.can(aclAbilities.action, aclAbilities.subject)) {
    return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
  }

  // Check the user that authorized and render the page
  if (isAuthorized) {
    return <>{children}</>
  }

  // Render Not Authorized component if the current user has limited access
  return (
    <BlankLayout>
      <NotAuthorized />
    </BlankLayout>
  )
}

export default AclGuard
